import React, { type ReactNode } from "react";
import { AccordionItem as ChakraAccordionItem, AccordionButton, AccordionPanel, Flex, Text } from "@chakra-ui/react";
import { remCalc } from "theme/utils";
import { ChevronDownIcon } from "ui/icons";
import { type Theme } from "contexts/color-theme";

export type AccordionItemProps = {
  /**
   * Determines if the item is the first in the accordion
   *
   * @default false
   */
  isFirst?: boolean;

  /**
   * Unique identifier for the accordion item
   *
   * Required if there are multiple item titles that are the same
   */
  id?: string;

  /**
   * Title of the accordion item
   */
  title: string;

  /**
   * The expandable content of the accordion item
   */
  content: ReactNode;

  /**
   * The color theme of the accordion item
   */
  theme?: Theme;
};

export function AccordionItem({ isFirst, title, content, theme }: AccordionItemProps) {
  return (
    <ChakraAccordionItem
      borderBottom="none"
      borderTop="1px"
      borderTopColor={isFirst ? "transparent" : `brand.border.${theme}`}
    >
      <AccordionButton
        borderRadius="tinier" // For focus states
        color={`brand.text.${theme}`}
        display="flex"
        alignItems="center"
        gap="small"
        justifyContent="space-between"
        textAlign="left"
        px={0}
        py="small"
        _hover={{ bg: "transparent" }}
        _expanded={{
          svg: { transform: "rotate(180deg)" },
        }}
      >
        <Text as="span" textStyle="headline3" fontSize="brand.md">
          {title}
        </Text>
        <Flex
          align="center"
          borderRadius="full"
          bg="brand.backgroundSecondary.light" // Always use light theme for buttons
          color="brand.text.light"
          boxSize={remCalc(42)}
          flexShrink={0}
          justify="center"
        >
          <ChevronDownIcon transition="transform 0.25s ease-in-out" />
        </Flex>
      </AccordionButton>
      <AccordionPanel
        color={`brand.textSecondary.${theme}`}
        textStyle="caption1"
        mb="small"
        p={0}
        pr={{ base: "bigger", md: "biggest" }}
      >
        {content}
      </AccordionPanel>
    </ChakraAccordionItem>
  );
}
